import { useEffect, useRef, useState } from 'react'
import { Document, Page } from '../pdf/pdfjs'
import { loadBlockPreview } from './blockPreviewCache'
import { colors, fontSize } from '../../styles/theme'

// A block renders on a full page; zoom past the usual 1in margins so its content fills the card.
const ZOOM = 1.4
const MARGIN_X = 0.07
const MARGIN_Y = 0.05

/** The top of a block's preview PDF (page 1), fetched once it scrolls into view. */
export default function BlockThumbnail({ contentBlockId, revision, name, width = 220, aspect = '8.5 / 5' }: {
  contentBlockId: string
  revision?: string
  name: string
  width?: number
  aspect?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { setVisible(true); io.disconnect() }
    }, { rootMargin: '200px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    if (!visible) return
    let cancelled = false
    setFailed(false)
    loadBlockPreview(contentBlockId, revision)
      .then(b => { if (!cancelled) setBlob(b) })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [visible, contentBlockId, revision])

  const placeholder = (text: string) => <div style={{ color: colors.textMuted, fontSize: fontSize.xs, alignSelf: 'center' }}>{text}</div>

  return (
    <div
      ref={ref}
      data-testid={`block-thumb-${contentBlockId}`}
      aria-label={`${name} preview`}
      role="img"
      style={{ aspectRatio: aspect, width: '100%', overflow: 'hidden', display: 'flex', alignItems: 'flex-start', justifyContent: blob && !failed ? 'flex-start' : 'center', background: '#fff' }}
    >
      {failed ? placeholder('Preview unavailable')
        : !blob ? placeholder(visible ? 'Loading…' : '')
        : (
          <div style={{ alignSelf: 'flex-start', marginLeft: -width * ZOOM * MARGIN_X, marginTop: -width * ZOOM * (11 / 8.5) * MARGIN_Y }}>
            <Document file={blob} loading={null} error={placeholder('Preview unavailable')}>
              <Page pageNumber={1} width={width * ZOOM} renderTextLayer={false} renderAnnotationLayer={false} loading={null} />
            </Document>
          </div>
        )}
    </div>
  )
}
